import React from 'react';
import * as R from 'ramda';

import {translate} from 'iss/strings';
import styles from 'iss/styles/ActivationList.styl';
import dates from 'iss/utils/dates';
import {SelectableTable} from 'iss/widgets/SelectableTable';
import {Table} from 'iss/widgets/bootstrap/Table';
import {formatLocationAddress, formatLocationNameAndCode, formatSiteAddress} from 'iss/widgets/format/Site';
import {SortColumnHeader} from 'iss/widgets/SortColumnHeader';

export function ActivationList({activations, selectable = false, compact = false, extraColumns = [], search = {}, onChangeSort, extraColumnsFirst = false, ...props}) {
  const TableComponent = selectable ? SelectableTable : Table;
  const {query, showArchived} = search;
  const columns = compact ? compactColumns : defaultColumns(query, showArchived, onChangeSort);
  return <TableComponent id="activations-list" data={activations}
    {...props}
    rowProps={({isActive}) => isActive ? {} : {className: "deleted"}}
    columns={getListColumns(columns, extraColumns, extraColumnsFirst)}
  />;
}

function getListColumns(columns, extraColumns, extraColumnsFirst) {
  if (extraColumns.length > 0) {

    if (extraColumnsFirst) {
      return [...extraColumns, ...columns];
    } else {
      return [...columns, ...extraColumns];
    }

  } else {
    return columns;
  }
}

// Something like this needed for updating query by clicking header buttons to sort by header, but this approach did not yet work
function HeaderButtons(text, field, query, showArchived, onChangeSort) {
  return <SortColumnHeader text={text} field={field} onChangeSort={(field, order) => onChangeSort({query, showArchived, sortFieldName: field, sortOrder: order})}/>;
}

const defaultColumns = (query, showArchived, onChangeSort) => {
  return [
    {
      header: HeaderButtons('activationList.activationId', 'ID', query, showArchived, onChangeSort),
      cellProps: {className: "activation-id " + styles.numberField },
      accessor: activation => activation.id + " " + showArchivedStatus(activation)
    }, {
      header: HeaderButtons('activationList.siteName', 'NAME', query, showArchived, onChangeSort),
      cellProps: {className: "activation-site-name" },
      accessor: ["contract", "site", "name"]
    }, {
      header: HeaderButtons('activationList.siteLocationName', 'LOCATION', query, showArchived, onChangeSort),
      cellProps: {className: "activation-location-name" },
      accessor: activation => activation.location && formatLocationNameAndCode(activation.location)
    }, {
      header: HeaderButtons('activationList.siteAddress', 'ADDRESS', query, showArchived, onChangeSort),
      cellProps: {className: "th-lg activation-address" },
      accessor: getSiteOrLocationAddress
    }, {
      header: HeaderButtons('activationList.formName', 'FORM_NAME', query, showArchived, onChangeSort),
      cellProps: {className: "activation-form-name" },
      accessor: ["evaluationForm", "name"]
    }, {
      header: HeaderButtons('activationList.latestEvent', 'LATEST_EVENT', query, showArchived, onChangeSort),
      cellProps: {className: "activation-latest-event" },
      accessor: activation => activation.latestEvent && dates.formatDateTime(activation.latestEvent)
    }, {
      header: HeaderButtons('activationList.nextEventDate', 'NEXT_EVENT_DATE', query, showArchived, onChangeSort),
      cellProps: {className: "activation-next-event-date" },
      accessor: activation => activation.nextEventDate && dates.format(activation.nextEventDate)
    }, {
      header: HeaderButtons('activationList.lateEventsCount', 'LATE_EVENTS_COUNT', query, showArchived, onChangeSort),
      cellProps: {className: "activation-late-events-count " +styles.numberField },
      accessor: activation => activation.lateEventsCount
    }, {
      header: HeaderButtons('activationList.recurrenceType', 'RECURRENCE_TYPE', query, showArchived, onChangeSort),
      cellProps: {className: "activation-recurrence-type"},
      accessor: getRecurrence
    }
  ];
};

const compactColumns = [
  {
    header: translate('activationList.activationId'),
    cellProps: {className: "activation-id" },
    accessor: activation => activation.id + " " + showArchivedStatus(activation)
  }, {
    header: translate('activationList.siteNameAndAddress'),
    cellProps: {className: "activation-site-name" },
    accessor: getCompactSiteOrLocation
  }, {
    header: translate('activationList.formName'),
    cellProps: {className: "activation-form-name" },
    accessor: ["evaluationForm", "name"]
  },
];

function getCompactSiteOrLocation({location, contract}) {
  const name = location == null ? contract.site.name : location.name;
  return <React.Fragment>
    {name}
    <br />
    <em style={{whiteSpace: "nowrap"}}>{getSiteOrLocationAddress({location, contract})}</em>
  </React.Fragment>;
}

function showArchivedStatus({isActive}) {
  return isActive ? "" : "("+translate("activationList.archived")+")";
}

function getSiteOrLocationAddress({location, contract}) {
  return (location && location.address) ? formatLocationAddress(location) + ' (' + formatSiteAddress(contract.site) + ')' : formatSiteAddress(contract.site);
}

function getRecurrence({recurrenceType, recurrenceDates}) {
  const type = recurrenceType ? translate(`domain.activatedEvaluation.recurrenceTypes.${recurrenceType}`) : '-';
  const dates = formatRecurrenceDates(recurrenceDates);
  return <React.Fragment>
    {type}<br />
    {dates}
  </React.Fragment>;
}

function formatRecurrenceDates(recurrenceDates) {
  return recurrenceDates ? R.join(', ', R.map(formatShortDateOrDay, recurrenceDates)): '';
}

function formatShortDateOrDay({month, day}) {
  return month ? `${day}.${month}.` : `${day}.`;
}



// WEBPACK FOOTER //
// ./modules/issFrontend/widgets/ActivationList.jsx